import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnsType } from 'antd/es/table';
import { Table, Link } from '@/ui';
import { formatPercentValue } from '@/utils';
import { ROUTES } from '@/models/router.model';
import { ICancelledPartnerItem } from '@/models/cancelledOrders.model';
import { sortCancelledPartnersByShare } from '../../helpers';
import DynamicsCell from '../DynamicsCell/DynamicsCell';
import { StyledFooterButton, StyledTableCard, StyledTableTitle } from './PartnersTableStyles';
import InfoLabel from '@/modules/Dashboard/components/InfoLabel';

interface IPartnersTableProps {
  items: ICancelledPartnerItem[];
  loading?: boolean;
  onViewAll?: () => void;
  limit?: number;
}

const PartnersTable = ({ items, loading, onViewAll, limit = 5 }: IPartnersTableProps) => {
  const { t, i18n } = useTranslation(['dashboard']);
  const ppSuffix = t('cancelled_orders_pp_suffix');

  const dataSource = useMemo(() => sortCancelledPartnersByShare(items).slice(0, limit), [items, limit]);

  const columns: ColumnsType<ICancelledPartnerItem> = [
    {
      title: '#',
      key: 'index',
      width: 32,
      align: 'center',
      render: (_value, _record, index) => index + 1,
    },
    {
      title: t('cancelled_orders_partner'),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (name: string, record) => <Link path={`${ROUTES.PARTNER_DETAILS_INDEX}/${record.id}`}>{name}</Link>,
    },
    {
      title: <InfoLabel label={t('cancelled_orders_share')} tooltip={t('cancelled_orders_partners_share_tooltip')} />,
      dataIndex: 'cancelled_share_percent',
      key: 'cancelled_share_percent',
      width: 152,
      render: (value: number) => formatPercentValue(value, 1, i18n.language),
    },
    {
      title: (
        <InfoLabel label={t('cancelled_orders_dynamics')} tooltip={t('cancelled_orders_partners_dynamics_tooltip')} />
      ),
      dataIndex: 'change_percent',
      key: 'change_percent',
      width: 112,
      render: (value: number | null) => <DynamicsCell value={value} suffix={ppSuffix} />,
    },
  ];

  return (
    <StyledTableCard>
      <StyledTableTitle>
        <InfoLabel
          label={t('cancelled_orders_partners_title')}
          tooltip={t('cancelled_orders_partners_title_tooltip')}
        />
      </StyledTableTitle>
      <Table rowKey="id" columns={columns} dataSource={dataSource} loading={loading} size="small" />
      {onViewAll ? (
        <StyledFooterButton type="default" onClick={onViewAll}>
          {t('cancelled_orders_view_all_partners')}
        </StyledFooterButton>
      ) : null}
    </StyledTableCard>
  );
};

export default PartnersTable;
